import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import images from "../../../../utilities/images/images";
import completedSetIcon from "../../../../utilities/images/adaptive_qbank/completed_set.png";
import {
  postAdaptiveUserAnalysisRequest,
  fetchAdaptiveSolutionRequest,
  fetchAdaptiveAllQuestionListRequest,
} from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import AdaptiveUpgradeModal from "../components/AdaptiveUpgradeModal";
import QuestionGridNumber from "../../../analytics/components/QuestionWise/QuestionGridNumber";
import "./AdaptiveSubmit.css";

const getRoot = (payload) => payload?.data ?? payload ?? {}; 

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.questions)) return value.questions;
  if (Array.isArray(value?.allquestionlist)) return value.allquestionlist;
  if (Array.isArray(value?.question_list)) return value.question_list;
  if (Array.isArray(value?.question_data)) return value.question_data;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const pickValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

/** Numeric fields from APIs (allows 0 without falling through pickValue skips). */
const pickCount = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
};

/** UserAdaptiveAnalysis may nest payloads under data/result after getRoot(). */
const getPerformanceCardsArray = (summaryRoot = {}) =>
  pickValue(
    summaryRoot.performance_cards,
    summaryRoot.performanceCards,
    summaryRoot.cards,
    summaryRoot.data?.performance_cards,
    summaryRoot.data?.performanceCards,
    summaryRoot.result?.performance_cards,
    summaryRoot.analysis?.performance_cards
  );

const parseFocusSetOrder = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  const str = String(value).trim();
  const trailingDigits = str.match(/(\d+)\s*$/);
  if (trailingDigits) {
    const n = Number(trailingDigits[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const digits = str.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeId = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const num = Number(value);
  if (Number.isFinite(num)) return String(num);
  const text = String(value).trim();
  return text ? text : null;
};

const normalizeText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();

const isSubtopicSelected = (item = {}) => {
  const selected = pickValue(item?.is_selected, item?.isSelected, item?.selected);
  return selected === true || Number(selected) === 1;
};

const getSubtopicName = (item = {}) =>
  pickValue(item?.subtopic_name, item?.sub_topic_name, item?.name, item?.topic_name, "");

const getSubtopicDifficulty = (item = {}) =>
  pickValue(item?.current_difficulty, item?.selecteddifficulty, item?.difficulty, item?.level, "");

const getQuestionSubtopicId = (q = {}) =>
  normalizeId(
    pickValue(q?.sub_topic_id, q?.subtopic_id, q?.topic_id, q?.topicId, q?.section_id)
  );

const getQuestionSubtopicName = (q = {}) =>
  normalizeText(
    pickValue(q?.subtopic_name, q?.sub_topic_name, q?.subTopicName, q?.topic_name, q?.section_name, "")
  );

const normalizeDifficultyLabel = (value) => {
  const raw = String(value ?? "").toLowerCase();
  if (raw.includes("hard")) return "Hard";
  if (raw.includes("easy")) return "Easy";
  if (raw.includes("med")) return "Medium";
  return value || "Medium";
};

const AQB_DEFAULT_PAPER_TYPE = 20;
const QUESTIONS_PER_SECTION = 5;
const TOTAL_SUMMARY_QUESTIONS = 10;

/** Merge quiz rows with adaptive solution/analytics (`fetchAdaptiveSolution`) so correctness flags match the response grid. */
const mergeAdaptiveQuestionsWithSolution = (questionRows, solutionPayload, limit = TOTAL_SUMMARY_QUESTIONS) => {
  const baseRows = questionRows.slice(0, limit);
  const solutionRoot = getRoot(solutionPayload);
  const analyticsRows = Array.isArray(solutionRoot?.data)
    ? solutionRoot.data
    : toArray(solutionRoot);
  if (!baseRows.length) return [];
  if (!analyticsRows.length) return baseRows;
  return baseRows.map((baseRow) => {
    const analyticsRow = analyticsRows.find((q) => String(q?.qid) === String(baseRow?.qid));
    if (!analyticsRow) return baseRow;
    return { ...baseRow, ...analyticsRow };
  });
};

const hasAttemptedAnswer = (q = {}) => {
  const selectedOptionRaw = pickValue(
    q?.selectedOption,
    q?.selected_option,
    q?.user_selected_option,
    q?.selected_option_id,
    q?.user_option_id
  );
  const selectedOptionIds = Array.isArray(selectedOptionRaw)
    ? selectedOptionRaw
    : selectedOptionRaw !== null &&
        selectedOptionRaw !== undefined &&
        String(selectedOptionRaw).trim() !== ""
      ? [selectedOptionRaw]
      : [];

  const integerAnswer = String(
    pickValue(q?.integerAnswer, q?.integer_Answer, q?.integer_answer, q?.option_int, "")
  ).trim();

  return selectedOptionIds.length > 0 || integerAnswer !== "";
};

const isQuestionCorrect = (q = {}) => {
  const directFlag = pickValue(
    q?.is_correct,
    q?.isCorrect,
    q?.correct,
    q?.user_answered_correct,
    q?.is_user_correct
  );
  if (directFlag === true || Number(directFlag) === 1) return true;
  if (directFlag === false || Number(directFlag) === 0) return false;

  const statusText = String(
    pickValue(q?.answer_status, q?.result, q?.user_result, q?.status_text, "")
  ).toLowerCase();
  if (statusText.includes("correct")) return true;
  if (statusText.includes("wrong") || statusText.includes("incorrect")) return false;

  const answerType = Number(q?.answer_type_id);
  if (answerType === 2 && Array.isArray(q?.option)) {
    const selectedRaw = pickValue(
      q?.selectedOption,
      q?.selected_option,
      q?.user_selected_option,
      q?.selected_option_id,
      q?.user_option_id
    );
    const selectedIds = Array.isArray(selectedRaw)
      ? selectedRaw.map((id) => String(id))
      : selectedRaw !== null && selectedRaw !== undefined && String(selectedRaw).trim() !== ""
        ? [String(selectedRaw)]
        : [];
    const correctIds = q.option
      .filter((opt) => Number(opt?.is_correct) === 1)
      .map((opt) => String(opt?.option_id));

    if (!selectedIds.length || !correctIds.length) return false;
    const selectedSet = new Set(selectedIds);
    const correctSet = new Set(correctIds);
    if (selectedSet.size !== correctSet.size) return false;
    return [...correctSet].every((id) => selectedSet.has(id));
  }

  if (answerType === 1 && Array.isArray(q?.option)) {
    const selectedId = String(
      pickValue(
        q?.selectedOption,
        q?.selected_option,
        q?.user_selected_option,
        q?.selected_option_id,
        q?.user_option_id,
        ""
      )
    ).trim();
    if (!selectedId) return false;
    return q.option.some(
      (opt) =>
        String(opt?.option_id) === selectedId && Number(opt?.is_correct) === 1
    );
  }

  if (answerType === 3) {
    const userAnswer = String(
      pickValue(q?.integerAnswer, q?.integer_Answer, q?.integer_answer, q?.option_int, "")
    ).trim();
    const correctAnswer = String(
      pickValue(q?.answer, q?.correct_answer, q?.answer_text, "")
    ).trim();
    if (!userAnswer || !correctAnswer) return false;

    const userNum = Number(userAnswer);
    const correctNum = Number(correctAnswer);
    if (Number.isFinite(userNum) && Number.isFinite(correctNum)) {
      return userNum === correctNum;
    }
    return userAnswer === correctAnswer;
  }

  return false;
};

const isQuestionWrong = (q = {}) => {
  if (!hasAttemptedAnswer(q)) return false;
  if (isQuestionCorrect(q)) return false;
  const wrongFlag = pickValue(q?.is_wrong, q?.isWrong, q?.incorrect);
  if (wrongFlag === true || Number(wrongFlag) === 1) return true;

  const statusText = String(
    pickValue(q?.answer_status, q?.result, q?.user_result, "")
  ).toLowerCase();
  if (statusText.includes("wrong") || statusText.includes("incorrect")) return true;

  return true;
};

const getQuestionBucketLabel = (q = {}, fallbackIndex = 0) =>
  pickValue(
    q?.subtopic_name,
    q?.sub_topic_name,
    q?.subTopicName,
    q?.topic_name,
    q?.topicName,
    q?.section_name,
    q?.sectionName,
    `Section ${fallbackIndex + 1}`
  );

const getQuestionDifficulty = (q = {}) => {
  const raw = String(
    pickValue(
      q?.difficulty,
      q?.difficulty_level,
      q?.difficultyLevel,
      q?.level,
      q?.current_difficulty,
      "Medium"
    )
  ).toLowerCase();

  if (raw.includes("hard")) return "Hard";
  if (raw.includes("easy")) return "Easy";
  return "Medium";
};

const getCardTone = (statusTag = "", score = 0, total = 0) => {
  const status = String(statusTag).toLowerCase();
  if (status.includes("fail")) {
    return "danger";
  }
  if (
    status.includes("crack") ||
    status.includes("master") ||
    status.includes("pass") ||
    (Number(total) > 0 && Number(score) / Number(total) >= 0.6)
  ) {
    return "success";
  }
  return "danger";
};

const isCrackedStatus = (statusTag = "") =>
  String(statusTag).toLowerCase().includes("crack");
const isFailedStatus = (statusTag = "") =>
  String(statusTag).toLowerCase().includes("fail");

const resolveAdaptiveIdentity = ({ routeTest, plannerDetails, testDetails, submitRoot }) => {
  const adaptiveQuestionId = pickValue(
    routeTest?.adaptive_question_id,
    routeTest?.adaptiveQuestionId,
    routeTest?.aqb_id,
    routeTest?.id,
    routeTest?.paper_id,
    submitRoot?.adaptive_question_id,
    testDetails?.adaptive_question_id,
    testDetails?.testId,
    testDetails?.paperId,
    plannerDetails?.planner_test_id
  );

  const paperType = Number(
    pickValue(
    routeTest?.paper_type,
    routeTest?.paperType,
    routeTest?.category_id,
    testDetails?.paperType,
    plannerDetails?.id,
    testDetails?.format,
    AQB_DEFAULT_PAPER_TYPE
    )
  );

  return {
    adaptive_question_id: adaptiveQuestionId || null,
    paper_type: paperType,
  };
};

/** Align with homepage: paid users (`is_user_free` false/0) get full solution access — no upgrade prompt. */
const isAdaptiveQBFreeTier = (payload) => {
  const raw = payload?.is_user_free;
  if (raw === true || Number(raw) === 1) return true;
  if (typeof raw === "string") {
    const t = raw.trim().toLowerCase();
    return t === "true" || t === "1";
  }
  return false;
};

const AdaptiveSubmit = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const routeState = location?.state ?? {};
  const routeTest = routeState?.test || routeState?.item || routeState;
  const routeSummarySelectedTopics = Array.isArray(routeState?.summarySelectedTopics)
    ? routeState.summarySelectedTopics
    : [];
  const isRouteCustomizationApplied = routeState?.isTopicCustomizationApplied === true;

  const {
    submitResult,
    userAnalysis,
    allQuestionList,
    solution,
    loading,
    error,
    adaptiveHomepage,
    subTopicList,
  } = useSelector((s) => s.adaptiveQBank);
  const { plannerDetails, testDetails } = useSelector((s) => s.examSlice);

  const routeFocusSetNo = Number(
    pickValue(
      routeState?.focusSetNo,
      routeTest?.focus_set_no,
      routeTest?.focusSetNo,
      1
    )
  );
  const routeAdaptiveQuestionId = pickValue(
    routeState?.adaptive_question_id,
    routeState?.adaptiveQuestionId,
    routeTest?.adaptive_question_id,
    routeTest?.adaptiveQuestionId
  );
  const submitRoot = useMemo(() => getRoot(submitResult), [submitResult]);
  const userAnalysisRoot = useMemo(() => getRoot(userAnalysis), [userAnalysis]);
  const summaryRoot = useMemo(() => {
    const analysisFocusSetNo = parseFocusSetOrder(
      pickValue(
        userAnalysisRoot?.focus_set_no,
        userAnalysisRoot?.focusSetNo,
        userAnalysisRoot?.focus_set_title,
        userAnalysisRoot?.focus_set_name
      )
    );
    const analysisAdaptiveId = pickValue(
      userAnalysisRoot?.adaptive_question_id,
      userAnalysisRoot?.adaptiveQuestionId
    );

    const hasRouteFocusSet = Number.isFinite(routeFocusSetNo) && routeFocusSetNo > 0;
    const hasAnalysisFocusSet = Number.isFinite(analysisFocusSetNo) && analysisFocusSetNo > 0;
    // If route explicitly requests a focus set, analysis must explicitly match it.
    const focusSetMatches = hasRouteFocusSet
      ? hasAnalysisFocusSet && analysisFocusSetNo === routeFocusSetNo
      : true;

    const hasRouteAdaptiveId =
      routeAdaptiveQuestionId !== undefined &&
      routeAdaptiveQuestionId !== null &&
      String(routeAdaptiveQuestionId).trim() !== "";
    const hasAnalysisAdaptiveId =
      analysisAdaptiveId !== undefined &&
      analysisAdaptiveId !== null &&
      String(analysisAdaptiveId).trim() !== "";
    const adaptiveIdMatches = !hasRouteAdaptiveId || !hasAnalysisAdaptiveId
      ? true
      : String(analysisAdaptiveId) === String(routeAdaptiveQuestionId);

    if (!loading?.userAnalysis && userAnalysis && focusSetMatches && adaptiveIdMatches) {
      return userAnalysisRoot;
    }
    return submitRoot;
  }, [
    loading?.userAnalysis,
    routeAdaptiveQuestionId,
    routeFocusSetNo,
    submitRoot,
    userAnalysis,
    userAnalysisRoot,
  ]);
  const questionList = useMemo(() => toArray(getRoot(allQuestionList)), [allQuestionList]);
  const questionsMergedWithSolution = useMemo(
    () => mergeAdaptiveQuestionsWithSolution(questionList, solution),
    [questionList, solution]
  );
  const adaptiveIdentity = useMemo(
    () =>
      resolveAdaptiveIdentity({
        routeTest,
        plannerDetails,
        testDetails,
        submitRoot,
      }),
    [plannerDetails, routeTest, submitRoot, testDetails]
  );
  const selectedFocusSetNoForAnalysis = useMemo(
    () =>
      Number(
        pickValue(
          routeState?.focusSetNo,
          routeTest?.focus_set_no,
          routeTest?.focusSetNo,
          userAnalysisRoot?.focus_set_no,
          userAnalysisRoot?.focusSetNo,
          0
        )
      ),
    [routeState, routeTest, userAnalysisRoot]
  );
  const homepageRoot = useMemo(() => getRoot(adaptiveHomepage), [adaptiveHomepage]);

  useEffect(() => {
    if (!adaptiveIdentity.adaptive_question_id) return;

    dispatch(
      postAdaptiveUserAnalysisRequest({
        adaptive_question_id: adaptiveIdentity.adaptive_question_id,
        focus_set_no:
          Number.isFinite(selectedFocusSetNoForAnalysis) && selectedFocusSetNoForAnalysis > 0
            ? selectedFocusSetNoForAnalysis
            : undefined,
      })
    );
  }, [adaptiveIdentity.adaptive_question_id, dispatch, selectedFocusSetNoForAnalysis]);

  useEffect(() => {
    if (!adaptiveIdentity.adaptive_question_id) return;
    dispatch(
      fetchAdaptiveAllQuestionListRequest({
        paper_type: AQB_DEFAULT_PAPER_TYPE,
        adaptive_question_id: adaptiveIdentity.adaptive_question_id,
        focus_set_no:
          Number.isFinite(routeFocusSetNo) && routeFocusSetNo > 0
            ? routeFocusSetNo
            : undefined,
      })
    );
  }, [adaptiveIdentity.adaptive_question_id, dispatch, routeFocusSetNo]);

  useEffect(() => {
    if (!adaptiveIdentity.adaptive_question_id) return;
    const paperType = Number(
      pickValue(adaptiveIdentity.paper_type, AQB_DEFAULT_PAPER_TYPE) || AQB_DEFAULT_PAPER_TYPE
    );
    if (!Number.isFinite(paperType) || paperType <= 0) return;

    dispatch(
      fetchAdaptiveSolutionRequest({
        paper_id: adaptiveIdentity.adaptive_question_id,
        paper_type: paperType,
      })
    );
  }, [adaptiveIdentity.adaptive_question_id, adaptiveIdentity.paper_type, dispatch]);

  const customizedSelectedSubtopics = useMemo(() => {
    const routeSelected = routeSummarySelectedTopics
      .slice(0, 2)
      .map((item, idx) => ({
        id: normalizeId(pickValue(item?.id, item?.sub_topic_id, item?.subtopic_id, idx + 1)),
        name: pickValue(item?.name, item?.subtopic_name, item?.sub_topic_name, `Section ${idx + 1}`),
        difficulty: normalizeDifficultyLabel(
          pickValue(item?.difficulty, item?.current_difficulty, item?.level, "Medium")
        ),
      }))
      .filter((item) => String(item?.name || "").trim() !== "");
    if (isRouteCustomizationApplied && routeSelected.length > 0) {
      return routeSelected;
    }

    const root = getRoot(subTopicList);
    const list = toArray(
      pickValue(
        root?.subtopics,
        root?.subtopic_list,
        root?.sub_topic,
        root?.topic_subtopics,
        root?.list,
        subTopicList?.data?.subtopics,
        subTopicList?.data?.subtopic_list,
        subTopicList?.subtopics,
        subTopicList?.subtopic_list
      )
    );
    return list.filter(isSubtopicSelected).slice(0, 2).map((item, idx) => ({
      id: normalizeId(
        pickValue(item?.sub_topic_id, item?.subtopic_id, item?.topic_id, item?.id, idx + 1)
      ),
      name: getSubtopicName(item),
      difficulty: normalizeDifficultyLabel(getSubtopicDifficulty(item)),
    }));
  }, [isRouteCustomizationApplied, routeSummarySelectedTopics, subTopicList]);

  const baseSummaryCards = useMemo(() => {
    const analysisCards = toArray(getPerformanceCardsArray(summaryRoot ?? {}));

    if (analysisCards.length > 0) {
      const analysisCardHasScores = (card = {}) =>
        pickCount(card.correct, card.Correct, card.correct_count, card.score, card.right) !==
          null ||
        pickCount(card.total, card.Total, card.total_questions, card.question_count) !== null;

      const hasUsableScoreInAnalysis = analysisCards.some(analysisCardHasScores);
      if (!hasUsableScoreInAnalysis) {
        // Some UserAdaptiveAnalysis payloads send only labels; compute scores from question list.
        const firstTenFallback =
          questionsMergedWithSolution.slice(0, TOTAL_SUMMARY_QUESTIONS);
        const fallbackSections = [
          firstTenFallback.slice(0, QUESTIONS_PER_SECTION),
          firstTenFallback.slice(QUESTIONS_PER_SECTION, TOTAL_SUMMARY_QUESTIONS),
        ].filter((section) => section.length > 0);

        return fallbackSections.map((sectionQuestions, idx) => {
          const score = sectionQuestions.filter((q) => isQuestionCorrect(q)).length;
          const total = sectionQuestions.length || QUESTIONS_PER_SECTION;
          const subtitle = score > 3 ? "Mastered" : "Needs Practice";
          const statusTag = score > 3 ? "Cracked" : "Failed";
          return {
            id: idx,
            title: getQuestionBucketLabel(sectionQuestions[0], idx),
            score,
            total,
            subtitle,
            statusTag,
            difficulty: getQuestionDifficulty(sectionQuestions[0]),
            tone: score > 3 ? "success" : "danger",
          };
        });
      }

      return analysisCards.slice(0, 2).map((card, idx) => {
        const rawScore =
          pickCount(card.correct, card.Correct, card.correct_count, card.score, card.right) ?? 0;
        const rawTotal =
          pickCount(card.total, card.Total, card.total_questions, card.question_count) ??
          QUESTIONS_PER_SECTION;
        const normalizedScore = Math.max(0, rawScore);
        const normalizedTotal = QUESTIONS_PER_SECTION;
        const capTotal =
          Number.isFinite(rawTotal) && rawTotal > 0 ? rawTotal : normalizedTotal;
        const safeScore = Math.min(normalizedScore, capTotal, normalizedTotal);
        const isCracked = safeScore > 3;

        return {
          id: idx,
          scoresFromAnalysis: true,
          title: pickValue(
            card.title,
            card.subtopic_name,
            card.sub_topic_name,
            card.name,
            `Section ${idx + 1}`
          ),
          score: safeScore,
          total: normalizedTotal,
          subtitle: pickValue(
            card.label,
            card.subtitle,
            card.status_label,
            isCracked ? "Mastered" : "Needs Practice"
          ),
          statusTag: String(
            pickValue(card.badge, card.status_tag, card.statusTag, isCracked ? "Cracked" : "Failed")
          ).trim(),
          difficulty: normalizeDifficultyLabel(
            pickValue(card.difficulty, card.difficulty_level, card.level, "Medium")
          ),
          tone: isCracked ? "success" : "danger",
        };
      });
    }

    const firstTen = questionsMergedWithSolution.slice(0, TOTAL_SUMMARY_QUESTIONS);
    const sections = [
      firstTen.slice(0, QUESTIONS_PER_SECTION),
      firstTen.slice(QUESTIONS_PER_SECTION, TOTAL_SUMMARY_QUESTIONS),
    ].filter((section) => section.length > 0);

    return sections.map((sectionQuestions, idx) => {
      const score = sectionQuestions.filter((q) => isQuestionCorrect(q)).length;
      const total = sectionQuestions.length || QUESTIONS_PER_SECTION;
      const subtitle = score > 3 ? "Mastered" : "Needs Practice";
      const statusTag = score > 3 ? "Cracked" : "Failed";

      return {
        id: idx,
        title: getQuestionBucketLabel(sectionQuestions[0], idx),
        score,
        total,
        subtitle,
        statusTag,
        difficulty: getQuestionDifficulty(sectionQuestions[0]),
        tone: score > 3 ? "success" : "danger",
      };
    });
  }, [questionsMergedWithSolution, summaryRoot]);

  const summaryCards = useMemo(() => {
    if (!customizedSelectedSubtopics.length) return baseSummaryCards;
    const topTenQuestions = questionsMergedWithSolution.slice(0, TOTAL_SUMMARY_QUESTIONS);

    return baseSummaryCards.map((baseCard, idx) => {
      const selectedSubtopic = customizedSelectedSubtopics[idx];
      if (!selectedSubtopic) return baseCard;

      if (baseCard.scoresFromAnalysis) {
        return {
          ...baseCard,
          title:
            pickValue(selectedSubtopic.name, baseCard.title, `Section ${idx + 1}`) ??
            baseCard.title,
          // Keep performance-card difficulty aligned with explicit user selection.
          difficulty: normalizeDifficultyLabel(
            pickValue(selectedSubtopic.difficulty, baseCard.difficulty, "Medium")
          ),
        };
      }

      const selectedName = normalizeText(selectedSubtopic.name);
      const selectedId = normalizeId(selectedSubtopic.id);
      const matchedQuestions = topTenQuestions.filter((q) => {
        const questionSubtopicId = getQuestionSubtopicId(q);
        const questionSubtopicName = getQuestionSubtopicName(q);
        if (selectedId && questionSubtopicId && selectedId === questionSubtopicId) return true;
        if (selectedName && questionSubtopicName && selectedName === questionSubtopicName) return true;
        return false;
      });

      const matchedScore = matchedQuestions.filter((q) => isQuestionCorrect(q)).length;
      const hasMatchedQuestions = matchedQuestions.length > 0;
      const score = hasMatchedQuestions ? matchedScore : baseCard.score;
      const total = hasMatchedQuestions ? matchedQuestions.length : baseCard.total;
      const isCracked = Number(score) > 3;

      return {
        ...baseCard,
        title: selectedSubtopic.name || baseCard.title,
        difficulty: normalizeDifficultyLabel(selectedSubtopic.difficulty || baseCard.difficulty),
        score,
        total,
        subtitle: isCracked ? "Mastered" : "Needs Practice",
        statusTag: isCracked ? "Cracked" : "Failed",
        tone: isCracked ? "success" : "danger",
      };
    });
  }, [baseSummaryCards, customizedSelectedSubtopics, questionsMergedWithSolution]);

  // `focus_set_no` / `focusSetNo` can sometimes come back as labels like "FS-2"
  // (or "Focus Set 2"), so avoid `Number(...)` which would yield `NaN`.
  const focusSetNo =
    parseFocusSetOrder(
      pickValue(
        routeState?.focusSetNo,
        routeTest?.focus_set_no,
        routeTest?.focusSetNo,
        summaryRoot?.focus_set_no,
        summaryRoot?.focusSetNo
      )
    ) ?? 1;

  const focusSetTitle = pickValue(
    summaryRoot?.focus_set_title,
    summaryRoot?.focus_set_name,
    Number.isFinite(focusSetNo) ? `Focus Set ${focusSetNo}` : null,
    routeTest?.test_name,
    routeTest?.paper_name,
    "Focus Set"
  );
  const homepageCurrentFocusSetNo = useMemo(
    () =>
      parseFocusSetOrder(
        pickValue(
          homepageRoot?.current_focus_set_no,
          homepageRoot?.currentFocusSetNo,
          homepageRoot?.current_focus_set,
          homepageRoot?.currentFocusSet,
          homepageRoot?.current_focus_set_label,
          homepageRoot?.currentFocusSetLabel
        )
      ),
    [homepageRoot]
  );
  const upgradePackageId = useMemo(
    () =>
      Number(
        pickValue(
          homepageRoot?.package_details?.package_id,
          homepageRoot?.package_details?.id,
          homepageRoot?.package_detail?.package_id,
          homepageRoot?.package_detail?.id,
          homepageRoot?.package_id,
          homepageRoot?.packageId
        )
      ) || null,
    [homepageRoot]
  );
  const analysisFocusSetNoFromTitle = useMemo(
    () =>
      parseFocusSetOrder(
        pickValue(summaryRoot?.focus_set_title, summaryRoot?.focus_set_name, focusSetTitle)
      ),
    [focusSetTitle, summaryRoot]
  );
  const shouldBlockNextFocusSet = useMemo(() => {
    if (!homepageCurrentFocusSetNo || !analysisFocusSetNoFromTitle) return false;
    return Math.abs(homepageCurrentFocusSetNo - analysisFocusSetNoFromTitle) > 1;
  }, [analysisFocusSetNoFromTitle, homepageCurrentFocusSetNo]);
  const isFreeUser = isAdaptiveQBFreeTier(summaryRoot);
  const questionTextById = useMemo(() => {
    const map = new Map();
    questionList.forEach((q) => {
      const questionText = pickValue(q?.question, q?.question_text, q?.text, "");
      if (!questionText) return;
      const ids = [
        q?.question_id,
        q?.qid,
        q?.id,
        q?.questionId,
        q?.ques_id,
        q?.que_id,
      ];
      ids.forEach((id) => {
        const normalized = normalizeId(id);
        if (normalized) map.set(normalized, questionText);
      });
    });
    return map;
  }, [questionList]);
  const questionTextByQNo = useMemo(() => {
    const map = new Map();
    questionList.slice(0, TOTAL_SUMMARY_QUESTIONS).forEach((q, idx) => {
      const text = pickValue(q?.question, q?.question_text, q?.text, "");
      if (!text) return;
      const qNo = Number(pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1));
      if (Number.isFinite(qNo) && qNo > 0) map.set(String(qNo), text);
      map.set(String(idx + 1), text);
    });
    return map;
  }, [questionList]);
  const revisitQuestions = useMemo(() => {
    const summaryNode = pickValue(summaryRoot?.summary, summaryRoot?.overall, {});
    const expectedWrongCount = Number(
      pickValue(summaryNode?.wrong, summaryNode?.incorrect, summaryRoot?.wrong, 0)
    );
    const hasAnalysisRevisitSource =
      summaryRoot?.questions_to_revisit !== undefined ||
      summaryRoot?.questionsToRevisit !== undefined;
    const revisitFromAnalysis = toArray(
      pickValue(
        summaryRoot?.questions_to_revisit?.list,
        summaryRoot?.questionsToRevisit?.list,
        summaryRoot?.questions_to_revisit,
        summaryRoot?.questionsToRevisit
      )
    );

    if (revisitFromAnalysis.length > 0) {
      const normalizedAnalysisRevisit = revisitFromAnalysis.map((q, idx) => ({
        id: pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1),
        text: pickValue(
          q?.question,
          q?.question_text,
          q?.text,
          questionTextById.get(normalizeId(pickValue(q?.question_id, q?.qid, q?.id, q?.questionId))),
          questionTextByQNo.get(
            String(Number(pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1)))
          ),
          `Question ID ${pickValue(q?.question_id, q?.qid, q?.id, idx + 1)}`
        ),
      }));
      return Number.isFinite(expectedWrongCount) && expectedWrongCount >= 0
        ? normalizedAnalysisRevisit.slice(0, expectedWrongCount)
        : normalizedAnalysisRevisit;
    }

    // If backend explicitly provides revisit payload (including empty list),
    // trust it and do not derive fallback revisit questions from questionList.
    if (hasAnalysisRevisitSource) {
      return [];
    }

    const wrongFromQuestionList = questionList
      .slice(0, TOTAL_SUMMARY_QUESTIONS)
      .filter((q) => isQuestionWrong(q))
      .map((q, idx) => ({
        id: pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1),
        text: pickValue(
          q?.question,
          q?.question_text,
          q?.text,
          `Question ID ${pickValue(q?.question_id, q?.qid, q?.id, idx + 1)}`
        ),
      }));

    if (Number.isFinite(expectedWrongCount) && expectedWrongCount > 0) {
      return wrongFromQuestionList.slice(0, expectedWrongCount);
    }
    return wrongFromQuestionList;
  }, [questionList, questionTextById, questionTextByQNo, summaryRoot]);

  const revisitDebug = useMemo(() => {
    const summaryNode = pickValue(summaryRoot?.summary, summaryRoot?.overall, {});
    const expectedWrongCount = Number(
      pickValue(summaryNode?.wrong, summaryNode?.incorrect, summaryRoot?.wrong, 0)
    );
    const analysisList = toArray(
      pickValue(
        summaryRoot?.questions_to_revisit?.list,
        summaryRoot?.questionsToRevisit?.list,
        summaryRoot?.questions_to_revisit,
        summaryRoot?.questionsToRevisit
      )
    );
    const topTen = questionList.slice(0, TOTAL_SUMMARY_QUESTIONS);
    const fallbackWrong = topTen.filter((q) => isQuestionWrong(q));
    const attemptedTopTen = topTen.filter((q) => hasAttemptedAnswer(q));

    return {
      focusSetNo,
      expectedWrongCount: Number.isFinite(expectedWrongCount) ? expectedWrongCount : 0,
      analysisListCount: analysisList.length,
      attemptedTopTenCount: attemptedTopTen.length,
      fallbackWrongCount: fallbackWrong.length,
      finalRevisitCount: revisitQuestions.length,
      // Keep sample tiny so console stays readable.
      analysisSample: analysisList.slice(0, 3).map((q) => ({
        q_no: pickValue(q?.q_no, q?.question_no, q?.display_number),
        question_id: pickValue(q?.question_id, q?.qid, q?.id),
        answer_status: q?.answer_status,
        result: q?.result,
        is_wrong: q?.is_wrong,
        incorrect: q?.incorrect,
      })),
    };
  }, [focusSetNo, questionList, revisitQuestions, summaryRoot]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    // Diagnostic logs to trace why revisit count differs from attempted/wrong stats.
    console.groupCollapsed(
      `[AQB Submit Debug] Focus Set ${revisitDebug.focusSetNo} revisit trace`
    );
    console.log("Expected wrong (summary.wrong):", revisitDebug.expectedWrongCount);
    console.log("Analysis list count:", revisitDebug.analysisListCount);
    console.log("Attempted in top 10:", revisitDebug.attemptedTopTenCount);
    console.log("Wrong from question list fallback:", revisitDebug.fallbackWrongCount);
    console.log("Final rendered revisit count:", revisitDebug.finalRevisitCount);
    console.table(revisitDebug.analysisSample);
    console.groupEnd();
  }, [revisitDebug]);

  const submitGridQuestions = useMemo(
    () => mergeAdaptiveQuestionsWithSolution(questionList, solution),
    [questionList, solution]
  );
  const navigateToNextFocusSet = () => {
    history.push({
      pathname: "/practice/adaptive-question-bank/next-focus-set",
      state: {
        topic: routeState?.topic || routeTest?.topic || null,
        topic_id:
          routeState?.topic?.topic_id ||
          routeState?.topic_id ||
          routeTest?.topic_id,
        topic_name:
          routeState?.topic?.topic_name ||
          routeState?.topic_name ||
          routeTest?.topic_name,
      },
    });
  };
  const startNextFocusSet = () => {
    const nextFocusSetNo =
      Number.isFinite(focusSetNo) && focusSetNo > 0 ? focusSetNo + 1 : 1;

    // Compute the next focus set's `adaptive_question_id` from the current
    // adaptive homepage payload so the next set test doesn't reuse the
    // previously-completed set's adaptive_question_id.
    const findAdaptiveQuestionIdForFocusSetOrder = (targetOrder) => {
      if (!targetOrder || !homepageRoot) return null;

      const rawSets = toArray(
        pickValue(
          homepageRoot?.focus_sets,
          homepageRoot?.focusSets,
          homepageRoot?.learning_cycle_sets,
          homepageRoot?.learningCycleSets,
          homepageRoot?.progress,
          homepageRoot?.progress_list,
          homepageRoot?.focus_set_progress
        )
      );

      const sets =
        rawSets.length > 0
          ? rawSets
          : // Fallback: flatten per-learning-cycle payload shape.
            toArray(pickValue(homepageRoot?.learning_cycles, homepageRoot?.learningCycles)).flatMap(
              (cycleEl) => {
                const cycleSets = toArray(
                  pickValue(
                    cycleEl?.focus_sets,
                    cycleEl?.focusSets,
                    cycleEl?.sets,
                    cycleEl?.focus_set_list,
                    cycleEl?.focusSetList
                  )
                );
                return cycleSets;
              }
            );

      for (let idx = 0; idx < sets.length; idx += 1) {
        const item = sets[idx];
        const orderCandidate = pickValue(
          item?.focus_set_no,
          item?.focusSetNo,
          item?.order,
          item?.id
        );
        const parsedOrder = parseFocusSetOrder(
          orderCandidate !== null && orderCandidate !== undefined ? orderCandidate : idx + 1
        );
        if (!parsedOrder || Number(parsedOrder) !== Number(targetOrder)) continue;

        const adaptiveQuestionId = Number(
          pickValue(
            item?.adaptive_question_id,
            item?.adaptiveQuestionId,
            item?.paper_id,
            item?.paperId,
            item?.aqb_id
          )
        );
        if (Number.isFinite(adaptiveQuestionId) && adaptiveQuestionId > 0) {
          return adaptiveQuestionId;
        }
      }
      return null;
    };

    const nextAdaptiveQuestionId = findAdaptiveQuestionIdForFocusSetOrder(nextFocusSetNo);
    const adaptiveQuestionIdOverride =
      Number.isFinite(nextAdaptiveQuestionId) && nextAdaptiveQuestionId > 0
        ? nextAdaptiveQuestionId
        : undefined;

    history.push({
      pathname: "/practice/adaptive-question-bank/next-focus-set",
      state: {
        topic: routeState?.topic || routeTest?.topic || null,
        topic_id:
          routeState?.topic?.topic_id || routeState?.topic_id || routeTest?.topic_id,
        topic_name:
          routeState?.topic?.topic_name || routeState?.topic_name || routeTest?.topic_name,
        focusSetNo: nextFocusSetNo,
        completedFocusSetNo:
          Number.isFinite(focusSetNo) && focusSetNo > 0 ? focusSetNo : undefined,
        // Trigger immediate start of this set after homepage state is ready.
        autoStartFocusSetNo: nextFocusSetNo,
        adaptive_question_id: adaptiveQuestionIdOverride,
        suppressResume: true,
        isPaused: false,
      },
    });
  };
  const handleUpgradeCtaClick = () => {
    setIsUpgradeModalOpen(false);
    if (upgradePackageId) {
      history.push(`/package/${upgradePackageId}`);
      return;
    }
    history.push("/packages");
  };

  return (
    <div className="adaptive-submit-page">
      <div className="adaptive-submit-header">
        <img
          src={images.new_backarrow}
          alt="Back"
          className="adaptive-submit-back"
          onClick={navigateToNextFocusSet}
        />
        <h2 className="adaptive-submit-header-title">{focusSetTitle}</h2>
      </div>

      <div className="adaptive-submit-content">
        <section className="adaptive-submit-main">
          {loading?.userAnalysis ? (
            <div className="adaptive-submit-state">Loading analysis...</div>
          ) : null}
          {error ? <div className="adaptive-submit-state error">{error}</div> : null}

          <div className="adaptive-submit-banner">
            <img
              className="adaptive-submit-banner-check"
              src={completedSetIcon}
              alt=""
              aria-hidden="true"
            />
            <h3>{`${focusSetTitle} Completed!`}</h3>
            <p>{summaryRoot?.message || "Great effort! Here's how you performed."}</p>
          </div>

          <div className="adaptive-submit-cards">
            {summaryCards.map((card) => (
              <article key={card.id} className={`adaptive-card ${card.tone}`}>
                <div className="adaptive-card-meta">
                  <span
                    className={`adaptive-card-tag ${
                      isCrackedStatus(card.statusTag)
                        ? "cracked"
                        : isFailedStatus(card.statusTag)
                          ? "failed"
                          : ""
                    }`}
                  >
                    {isCrackedStatus(card.statusTag) ? (
                      <span className="adaptive-card-tag-icon" aria-hidden="true">
                        <img src={images.tick_icon} alt="" />
                      </span>
                    ) : isFailedStatus(card.statusTag) ? (
                      <span className="adaptive-card-tag-icon-failed" aria-hidden="true">
                        <span>×</span>
                      </span>
                    ) : null}
                    {card.statusTag}
                  </span>
                  <span className="adaptive-card-difficulty">{card.difficulty}</span>
                </div>
                <h4>{card.title}</h4>
                <p className="adaptive-card-score">
                  <strong>{card.score}</strong>
                  <span className="adaptive-card-score-total">/{card.total}</span>
                  <span className="adaptive-card-score-label">Q&apos;s Correct</span>
                </p>
                <p className="adaptive-card-subtitle">{card.subtitle}</p>
              </article>
            ))}
          </div>

          <div className="adaptive-submit-revisit">
            <h4>{`Questions to Revisit (${revisitQuestions.length})`}</h4>
            <div className="adaptive-submit-revisit-list">
              {revisitQuestions.length === 0 ? (
                <div className="adaptive-submit-empty">No revisit questions found.</div>
              ) : (
                revisitQuestions.map((item) => (
                  <div key={item.id} className="adaptive-submit-revisit-item">
                    <span className="adaptive-submit-revisit-q">{`Q. ${item.id}`}</span>
                    <p>{item.text || "-"}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="adaptive-submit-actions">
            <button
              type="button"
              className="adaptive-submit-btn secondary"
              onClick={navigateToNextFocusSet}
            >
              Back
            </button>
            <button
              type="button"
              className={`adaptive-submit-btn primary ${
                shouldBlockNextFocusSet ? "disabled" : ""
              }`}
              onClick={startNextFocusSet}
              disabled={shouldBlockNextFocusSet}
              title={
                shouldBlockNextFocusSet
                  ? "Please continue from your current focus set on the adaptive homepage."
                  : undefined
              }
            >
              Next Focus Set
            </button>
          </div>
        </section>

        <aside className="adaptive-submit-grid-panel">
          {loading?.solution ? (
            <div className="adaptive-submit-grid-loading">Loading response grid…</div>
          ) : (
            <QuestionGridNumber
              selectedIndex={-1}
              questions={submitGridQuestions}
              onSelect={(index) => {
                if (isFreeUser) {
                  setIsUpgradeModalOpen(true);
                  return;
                }
                const selectedQuestion = submitGridQuestions[index];
                if (!selectedQuestion?.qid || !adaptiveIdentity.adaptive_question_id) return;
                history.push("/practice-view-solution", {
                  isAdaptiveQBank: true,
                  paperType: adaptiveIdentity.paper_type || AQB_DEFAULT_PAPER_TYPE,
                  paper_type: adaptiveIdentity.paper_type || AQB_DEFAULT_PAPER_TYPE,
                  paperId: adaptiveIdentity.adaptive_question_id,
                  adaptive_question_id: adaptiveIdentity.adaptive_question_id,
                  selectedQid: selectedQuestion.qid,
                  focusSetNo: Number.isFinite(focusSetNo) ? focusSetNo : routeState?.focusSetNo,
                  topic: routeState?.topic,
                  topic_id: routeState?.topic_id,
                  topic_name: routeState?.topic_name,
                });
              }}
            />
          )}
        </aside>
      </div>
      <AdaptiveUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onCtaClick={handleUpgradeCtaClick}
      />
    </div>
  );
};

export default AdaptiveSubmit;
